import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Alert,
  Space,
  Row,
  Col,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { PageHeader, DataTable, LoadingSkeleton } from '../design-system';

const { Option } = Select;
const { TextArea } = Input;

interface PaymentMethod {
  id: number;
  name: string;
  description: string;
  type: 'cash' | 'momo' | 'zalopay' | 'card' | 'bank' | 'atm';
  icon: string;
  is_active: boolean;
  fee: number;
  fee_description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PaymentMethodForm {
  name: string;
  description: string;
  type: 'cash' | 'momo' | 'zalopay' | 'card' | 'bank' | 'atm';
  icon: string;
  is_active: boolean;
  fee: number;
  fee_description: string;
  sort_order: number;
}

const PaymentMethods: React.FC = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(
    null
  );
  const [form] = Form.useForm();

  // Khớp paymentMethodService.VALID_TYPES và app user checkout.
  const paymentTypes = [
    { value: 'cash', label: 'COD — Thanh toán khi nhận hàng' },
    { value: 'momo', label: 'MoMo' },
    { value: 'zalopay', label: 'ZaloPay' },
    { value: 'bank', label: 'Thanh toán ví điện tử / tài khoản ngân hàng' },
    { value: 'atm', label: 'Thẻ ngân hàng (ATM / iBanking)' },
    { value: 'card', label: 'Thẻ tín dụng (Visa / MasterCard)' },
  ];

  const icons = [
    { value: 'money', label: 'Tiền mặt' },
    { value: 'account_balance', label: 'Ngân hàng' },
    { value: 'account_balance_wallet', label: 'Ví điện tử' },
    { value: 'payment', label: 'Thanh toán' },
    { value: 'credit_card', label: 'Thẻ tín dụng' },
    { value: 'qr_code', label: 'Mã QR' },
  ];

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payment-methods');
      setPaymentMethods(response.data.data);
      setError(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message ||
          'Không thể tải danh sách phương thức thanh toán'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (method?: PaymentMethod) => {
    if (method) {
      setEditingMethod(method);
      form.setFieldsValue({
        name: method.name,
        description: method.description,
        type: method.type,
        icon: method.icon,
        is_active: method.is_active,
        fee: method.fee,
        fee_description: method.fee_description || '',
        sort_order: method.sort_order,
      });
    } else {
      setEditingMethod(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingMethod(null);
    form.resetFields();
  };

  const handleSubmit = async (values: PaymentMethodForm) => {
    try {
      const payload = {
        ...values,
        fee: Number(values.fee) || 0,
        sort_order: Number(values.sort_order) || 0,
      };
      if (editingMethod) {
        await api.put(`/payment-methods/${editingMethod.id}`, payload);
        message.success('Cập nhật phương thức thanh toán thành công');
      } else {
        await api.post('/payment-methods', payload);
        message.success('Thêm phương thức thanh toán thành công');
      }
      handleCloseModal();
      loadPaymentMethods();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(
        error.response?.data?.message || 'Không thể lưu phương thức thanh toán'
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/payment-methods/${id}`);
      message.success('Xóa phương thức thanh toán thành công');
      loadPaymentMethods();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(
        error.response?.data?.message || 'Không thể xóa phương thức thanh toán'
      );
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await api.put(`/payment-methods/${id}/toggle-status`);
      message.success('Cập nhật trạng thái thành công');
      loadPaymentMethods();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(
        error.response?.data?.message || 'Không thể thay đổi trạng thái'
      );
    }
  };

  const getTypeLabel = (type: string) => {
    const found = paymentTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getIconLabel = (icon: string) => {
    const found = icons.find(i => i.value === icon);
    return found ? found.label : icon;
  };

  const columns = [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => getTypeLabel(type),
    },
    {
      title: 'Phí',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee: number) => `${fee.toLocaleString()} VNĐ`,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'sort_order',
      key: 'sort_order',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: PaymentMethod) => (
        <Button
          type="text"
          icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          onClick={() => handleToggleStatus(record.id)}
          aria-label={isActive ? 'Vô hiệu hóa' : 'Kích hoạt'}
        >
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Button>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: unknown, record: PaymentMethod) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
            aria-label={`Sửa phương thức ${record.name}`}
          >
            Sửa
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Xác nhận xóa',
                content:
                  'Bạn có chắc chắn muốn xóa phương thức thanh toán này?',
                onOk: () => handleDelete(record.id),
              });
            }}
            aria-label={`Xóa phương thức ${record.name}`}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Quản lý phương thức thanh toán"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
            aria-label="Thêm phương thức thanh toán"
          >
            Thêm phương thức
          </Button>
        }
      />

      {error && (
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
      >
        {loading ? (
          <LoadingSkeleton rows={5} showCard={false} />
        ) : (
          <DataTable
            columns={columns}
            dataSource={paymentMethods}
            rowKey="id"
            loading={loading}
            pagination={
              paymentMethods.length > 0
                ? {
                    current: 1,
                    total: paymentMethods.length,
                    pageSize: 10,
                    onChange: () => {
                      // Do nothing - client-side pagination handled by DataTable
                    },
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total: number, range: [number, number]) =>
                      `${range[0]}-${range[1]} của ${total} phương thức`,
                  }
                : false
            }
          />
        )}
      </Card>

      <Modal
        title={
          editingMethod
            ? 'Sửa phương thức thanh toán'
            : 'Thêm phương thức thanh toán'
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: 'cash',
            icon: 'payment',
            is_active: true,
            fee: 0,
            sort_order: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên phương thức"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên phương thức' },
                ]}
              >
                <Input placeholder="Nhập tên phương thức" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Loại phương thức"
                rules={[
                  { required: true, message: 'Vui lòng chọn loại phương thức' },
                ]}
              >
                <Select placeholder="Chọn loại phương thức">
                  {paymentTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Mô tả">
            <TextArea rows={2} placeholder="Nhập mô tả" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="icon"
                label="Icon"
                rules={[{ required: true, message: 'Vui lòng chọn icon' }]}
              >
                <Select placeholder="Chọn icon">
                  {icons.map(icon => (
                    <Option key={icon.value} value={icon.value}>
                      {icon.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fee" label="Phí (VNĐ)">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fee_description" label="Mô tả phí">
                <Input placeholder="Mô tả về phí" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sort_order" label="Thứ tự">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="Trạng thái"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Hoạt động"
              unCheckedChildren="Không hoạt động"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                aria-label={
                  editingMethod ? 'Cập nhật phương thức' : 'Thêm phương thức'
                }
              >
                {editingMethod ? 'Cập nhật' : 'Thêm'}
              </Button>
              <Button onClick={handleCloseModal} aria-label="Hủy">
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentMethods;
